import type {
  ChangeEvent,
  ComponentPropsWithoutRef,
  CSSProperties,
  KeyboardEvent,
  OptionHTMLAttributes,
  ReactNode,
} from 'react';
import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { getTovIconClass } from '../../contracts/iconography';
import { cn } from '../../lib/utils';
import { msg } from '../../localization/i18n';
import { useDialogPortalContainer } from '../TovDialogPortalContext/TovDialogPortalContext';

interface ParsedSelectOption {
  disabled?: boolean;
  label: ReactNode;
  value: string;
}

export interface SelectProps extends Omit<
  ComponentPropsWithoutRef<'select'>,
  'children' | 'multiple' | 'size'
> {
  children?: ReactNode;
  placeholder?: ReactNode;
}

export function Select({
  className,
  children,
  defaultValue,
  disabled,
  id,
  name,
  onBlur,
  onChange,
  placeholder,
  required,
  value,
  ...props
}: SelectProps) {
  const triggerButtonProps = props as Omit<
    ComponentPropsWithoutRef<'button'>,
    'children' | 'defaultValue' | 'name' | 'onChange' | 'value'
  >;
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const listboxId = `${selectId}-listbox`;
  const portalContainer = useDialogPortalContainer();
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const openTimeoutRef = useRef<number | null>(null);
  const options = useMemo<ParsedSelectOption[]>(
    () =>
      Children.toArray(children).flatMap((child) => {
        if (!isValidElement<OptionHTMLAttributes<HTMLOptionElement>>(child)) {
          return [];
        }

        return [
          {
            disabled: child.props.disabled,
            label: child.props.children,
            value: String(child.props.value ?? ''),
          },
        ];
      }),
    [children],
  );
  const [internalValue, setInternalValue] = useState(
    defaultValue !== undefined ? String(defaultValue) : '',
  );
  const [open, setOpen] = useState(false);
  const [contentStyle, setContentStyle] = useState<CSSProperties | null>(null);
  const currentValue =
    value !== undefined ? String(value ?? '') : internalValue;
  const selectedOption =
    options.find((option) => option.value === currentValue) ?? null;
  const selectedIndex = options.findIndex(
    (option) => option.value === currentValue,
  );
  const [activeIndex, setActiveIndex] = useState(
    selectedIndex >= 0 ? selectedIndex : 0,
  );
  const activeOption = options[activeIndex] ?? null;

  const updateContentPosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();

    if (!rect) {
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 8;
    const offset = 6;
    const availableBelow = viewportHeight - rect.bottom - margin - offset;
    const availableAbove = rect.top - margin - offset;
    const placeAbove = availableBelow < 160 && availableAbove > availableBelow;
    const maxHeight = Math.max(
      96,
      Math.min(320, placeAbove ? availableAbove : availableBelow),
    );
    const contentRect = contentRef.current?.getBoundingClientRect();
    const contentWidth = contentRect?.width ?? rect.width;
    const contentHeight = contentRect?.height ?? maxHeight;
    const width = Math.min(rect.width, viewportWidth - margin * 2);
    const left = Math.min(
      Math.max(rect.left, margin),
      viewportWidth - Math.max(width, contentWidth) - margin,
    );

    setContentStyle({
      left,
      maxHeight,
      minWidth: width,
      top: placeAbove
        ? Math.max(margin, rect.top - offset - contentHeight)
        : rect.bottom + offset,
    });
  }, []);

  const closeListbox = () => {
    if (openTimeoutRef.current !== null) {
      window.clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }

    setOpen(false);
  };

  const openListbox = () => {
    if (typeof window === 'undefined') {
      setOpen(true);
      return;
    }

    if (openTimeoutRef.current !== null) {
      window.clearTimeout(openTimeoutRef.current);
    }

    openTimeoutRef.current = window.setTimeout(() => {
      openTimeoutRef.current = null;
      updateContentPosition();
      setOpen(true);
    }, 0);
  };

  useEffect(() => {
    return () => {
      if (openTimeoutRef.current !== null) {
        window.clearTimeout(openTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;

      if (
        !rootRef.current?.contains(target) &&
        !contentRef.current?.contains(target)
      ) {
        closeListbox();
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    updateContentPosition();

    window.addEventListener('resize', updateContentPosition);
    window.addEventListener('scroll', updateContentPosition, true);

    return () => {
      window.removeEventListener('resize', updateContentPosition);
      window.removeEventListener('scroll', updateContentPosition, true);
    };
  }, [open, updateContentPosition]);

  useLayoutEffect(() => {
    if (open) {
      updateContentPosition();
    }
  }, [open, updateContentPosition]);

  useEffect(() => {
    if (!open) {
      setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [open, selectedIndex]);

  const dispatchValueChange = (nextValue: string) => {
    if (value === undefined) {
      setInternalValue(nextValue);
    }

    if (!onChange) {
      return;
    }

    const event = {
      currentTarget: {
        id: selectId,
        name,
        value: nextValue,
      },
      target: {
        id: selectId,
        name,
        value: nextValue,
      },
    } as unknown as ChangeEvent<HTMLSelectElement>;

    onChange(event);
  };

  const selectOptionAtIndex = (nextIndex: number) => {
    const option = options[nextIndex];

    if (!option || option.disabled) {
      return;
    }

    dispatchValueChange(option.value);
    closeListbox();
    triggerRef.current?.focus();
  };

  const scheduleOptionSelect = (nextIndex: number) => {
    if (typeof window === 'undefined') {
      selectOptionAtIndex(nextIndex);
      return;
    }

    window.setTimeout(() => selectOptionAtIndex(nextIndex), 0);
  };

  const moveActiveIndex = (direction: 1 | -1) => {
    if (options.length === 0) {
      return;
    }

    let nextIndex = activeIndex;

    for (let step = 0; step < options.length; step += 1) {
      nextIndex = (nextIndex + direction + options.length) % options.length;

      if (!options[nextIndex]?.disabled) {
        setActiveIndex(nextIndex);
        return;
      }
    }
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        openListbox();
      } else {
        moveActiveIndex(1);
      }
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        openListbox();
      } else {
        moveActiveIndex(-1);
      }
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!open) {
        openListbox();
      } else {
        selectOptionAtIndex(activeIndex);
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeListbox();
    }
  };

  const listbox =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="tov-select__content"
            id={listboxId}
            ref={contentRef}
            role="listbox"
            style={contentStyle ?? undefined}
            tabIndex={-1}
          >
            {options.map((option, index) => (
              <div
                aria-disabled={option.disabled}
                aria-selected={option.value === currentValue}
                className={cn(
                  'tov-select__item',
                  option.value === currentValue && 'tov-select__item--selected',
                )}
                data-disabled={option.disabled ? '' : undefined}
                data-highlighted={index === activeIndex ? '' : undefined}
                id={`${selectId}-option-${index}`}
                key={`${selectId}-${option.value}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  scheduleOptionSelect(index);
                }}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onMouseEnter={() => {
                  if (!option.disabled) {
                    setActiveIndex(index);
                  }
                }}
                role="option"
              >
                <span className="tov-select__item-label">{option.label}</span>
                {option.value === currentValue ? (
                  <span
                    className={cn('tov-icon', getTovIconClass('check'))}
                    aria-hidden="true"
                  />
                ) : null}
              </div>
            ))}
          </div>,
          portalContainer ?? document.body,
        )
      : null;

  return (
    <span className="tov-select__root" ref={rootRef}>
      <input
        name={name}
        type="hidden"
        value={currentValue}
        disabled={disabled}
        {...(required ? { 'data-required': 'true' } : {})}
      />
      <button
        {...triggerButtonProps}
        aria-activedescendant={
          open && activeOption ? `${selectId}-option-${activeIndex}` : undefined
        }
        aria-controls={open ? listboxId : undefined}
        aria-disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn('tov-select', className)}
        data-placeholder={
          selectedOption === null && placeholder !== undefined
            ? 'true'
            : undefined
        }
        disabled={disabled}
        id={selectId}
        onBlur={(event) => {
          onBlur?.(event as unknown as Parameters<typeof onBlur>[0]);
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();

          if (open) {
            closeListbox();
          } else {
            openListbox();
          }
        }}
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
        onKeyDown={handleTriggerKeyDown}
        ref={triggerRef}
        role="combobox"
        type="button"
      >
        <span className="tov-select__value">
          {selectedOption?.label ??
            placeholder ??
            options[0]?.label ??
            msg('general.actions.select')}
        </span>
        <span
          className={cn(
            'tov-select__caret tov-icon',
            getTovIconClass('caret-down'),
          )}
          aria-hidden="true"
        />
      </button>
      {listbox}
    </span>
  );
}

export function SelectOption(props: OptionHTMLAttributes<HTMLOptionElement>) {
  return <>{props.children}</>;
}
